/**
 * CANONICAL fetch-based HTTP client.
 *
 * As of 2026-05-12 this is the ONLY HTTP transport for `components/**`
 * (zero non-deliberate raw `fetch()` calls remain; only MockDataBanner.tsx
 * keeps its own pre-auth cache, and AuthContext.tsx bootstraps the token
 * that apiClient consumes). Per-domain api modules under `./api/*.ts` also
 * use this client; the legacy axios dependency is gone — `services/api.ts`
 * is now a 65-LOC barrel that re-exports from per-domain modules.
 *
 * Features:
 *   - Structured `ApiResponse<T>` envelope (data | error | status | code | details | cid)
 *   - Wiz error-envelope parsing: server `{error: {code, message, details}}`
 *   - Auto-injected headers: Authorization (Bearer), X-Tenant-ID, X-Correlation-ID
 *   - withRetry on idempotent GETs (5xx/0/408 → exp-backoff)
 *   - Per-request `timeoutMs` override (for health probes etc.)
 *   - `rawBody: true` for CSV/Blob/FormData uploads (skips JSON.stringify)
 */
import { API_CONFIG } from './config';

// Dev-only console diagnostics. The production build (NODE_ENV==='production')
// must stay silent: logging every request — and especially multi-MB response
// bodies like /api/v1/issues — freezes DevTools and bloats memory.
const DEBUG = process.env.NODE_ENV !== 'production';

// Wiz error envelope: { error: { code, message, details } }
// Mirrors shared/app_factory `_wiz_http_exc_handler`.
export interface WizErrorEnvelope {
  code: string;
  message: string;
  details: unknown | null;
}

export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  status: number;
  errorCode?: string;
  errorDetails?: unknown;
  correlationId?: string;
  loading?: boolean;
  // R196 (2026-06-04): gateway-flagged degraded response. When true, the
  // payload is a fallback stub (empty list / zeros) because the upstream
  // service was unreachable. Pages should render "Service offline" instead
  // of treating the empty payload as truth.
  isFallback?: boolean;
  fallbackReason?: string;
  fallbackUpstream?: string;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  retry?: boolean;
  /** Per-request timeout in ms. Overrides the client-level timeout. */
  timeoutMs?: number;
  /**
   * If true, send `body` verbatim without JSON.stringify. Caller must supply
   * the right `Content-Type` header (e.g. `text/csv`, `multipart/form-data`).
   * If `body` is a `FormData` or `Blob` and `rawBody` is true, the default
   * `Content-Type: application/json` is removed so the browser can set the
   * correct multipart boundary.
   */
  rawBody?: boolean;
}

// Global 401 handling. When any authenticated request comes back 401 the
// session token is stale/expired: clear it once and broadcast a single
// `auth:session-expired` event so AuthContext can re-validate (dev auto-login)
// or drop the user to the login page (prod). Debounced so a burst of parallel
// requests failing at once only fires the flow a single time — Wiz keeps one
// coherent session, not N scattered per-panel failures.
let sessionExpiredSignalled = false;
function handleUnauthorized(): void {
  if (sessionExpiredSignalled) return;
  sessionExpiredSignalled = true;
  try {
    localStorage.removeItem('access_token');
  } catch { /* storage disabled — event still fires */ }
  try {
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
  } catch { /* non-browser env */ }
  // Allow the flow to fire again on the next fresh session's expiry.
  setTimeout(() => { sessionExpiredSignalled = false; }, 3000);
}

/**
 * Global "a request failed" signal.
 *
 * Every panel in the app funnels through `request()`, and ~250 call sites
 * discard the failure with `.catch(() => [])` / `.catch(() => null)`. The result
 * measured under fault injection: with the whole API returning 500, most routes
 * still render numbers, zeros and empty tables with no indication anything is
 * wrong. In a security product that is the worst possible output — an operator
 * reads "0 critical findings" and acts on it.
 *
 * Fixing 250 call sites individually is both a large diff and a rule that
 * decays the next time someone writes a new panel. Instead the client — the one
 * place that *knows* a request failed — broadcasts it, and a single listener in
 * the shell surfaces it. A page can then still swallow its own error; it can no
 * longer make the app silent.
 *
 * Modelled on `handleUnauthorized` above, but deliberately NOT debounced here.
 * A 1.5s debounce in this function was measured to lose whole pages: /compliance
 * fans out 82 calls in a burst, and if the first one lands before the listener
 * has mounted (or inside the window left by the previous route), every
 * subsequent failure in that burst is swallowed and the page goes back to
 * lying. Coalescing is the listener's job — it knows when it is mounted and
 * which route it is on. This function's only contract is: never lose a failure.
 *
 * `recentFailures` covers the remaining race: a failure raised before the
 * listener mounts is still readable by it on mount.
 */
export interface ApiFailureDetail {
  endpoint: string;
  status: number;
  message: string;
  correlationId?: string;
  at: number;
}
const recentFailures: ApiFailureDetail[] = [];
/** Failures seen in the last `windowMs` — lets a late-mounting listener catch up. */
export function drainRecentFailures(windowMs = 15000): ApiFailureDetail[] {
  const cutoff = Date.now() - windowMs;
  return recentFailures.filter(f => f.at >= cutoff);
}
export function clearRecentFailures(): void {
  recentFailures.length = 0;
}
/**
 * Report a failure that did NOT come through this client.
 *
 * A handful of components fetch over `shared/hooks/useAuthFetch`'s `authFetch`
 * rather than ApiClient, so they never reach the funnel in `request()` — /easm
 * was the one route still rendering "0 Total Assets / 0 High Risk" under total
 * API failure after every other route had been fixed. Any alternative transport
 * must call this so the shell still learns the page is incomplete.
 */
export function reportRequestFailure(endpoint: string, status: number, message: string): void {
  signalRequestFailure({ endpoint, status, message, at: Date.now() });
}
function signalRequestFailure(detail: ApiFailureDetail): void {
  // 401 is the session-expiry flow's business (handleUnauthorized) — it
  // navigates the user, so a banner about it would be noise on the way out.
  if (detail.status === 401) return;
  // Only infrastructure failures: network (0), timeout (408) and 5xx. A 403/404
  // is usually an optional capability probe, and a banner that is always on is
  // one nobody reads.
  const infra = detail.status === 0 || detail.status === 408 || detail.status >= 500;
  if (!infra) return;
  recentFailures.push(detail);
  // Bounded: a backend down for a while must not grow this without limit.
  if (recentFailures.length > 200) recentFailures.splice(0, recentFailures.length - 200);
  try {
    window.dispatchEvent(new CustomEvent<ApiFailureDetail>('api:request-failed', { detail }));
  } catch { /* non-browser env (tests, SSR) */ }
}

// UUID-v4-ish correlation id (browser may not have crypto.randomUUID on old engines).
function newCorrelationId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Endpoints that paginate on `limit` and do NOT declare `page_size`.
 *
 * FastAPI silently drops an undeclared query param, so sending `page_size` to
 * one of these never errored — the handler just used its own default (or its
 * 500-row cap) and returned the whole table. Measured live against the deployed
 * gateway on 2026-07-26, asking for a single row:
 *
 *   /api/v1/attack-paths            ?page_size=1  3.96 MB   ?limit=1  8.3 KB
 *   /api/v1/issues                                1.35 MB             45.0 KB
 *   /api/v1/ciem/identities                        381 KB              3.4 KB
 *   /api/v1/graph/crown-jewels                     175 KB              3.3 KB
 *   /api/v1/cwpp/containers/images                 111 KB              1.1 KB
 *   /api/v1/admin/audit/events                    21.9 KB              469 B
 *
 * The user-visible cost was not just bytes: /api/v1/attack-paths?page_size=200
 * took 13.0s to first byte versus 1.36s for ?limit=200, which is why the Attack
 * Paths page sat on "Loading attack paths…" and why ExposureManagement's 6s
 * withTimeout() always expired and fell back to an empty list.
 *
 * Other endpoints (vulnerabilities, policies, kspm/workloads, ciem/anomalies)
 * are the mirror image -- they declare `page_size` and ignore `limit` -- so this
 * translation is deliberately a per-endpoint allow-list, not a global rename.
 * Verify with a real request before adding an entry.
 */
const LIMIT_PAGINATED_ENDPOINTS = [
  '/api/v1/attack-paths',
  '/api/v1/issues',
  '/api/v1/ciem/identities',
  '/api/v1/graph/crown-jewels',
  '/api/v1/cwpp/containers/images',
  '/api/v1/admin/audit/events',
];

/**
 * Rewrites `page_size` to `limit` for the endpoints above. Operates on the
 * parsed URL so it covers both call styles: a params object AND a query string
 * hand-built into the endpoint literal. An explicit `limit` always wins.
 */
export function normalizePageSizeParam(url: URL): void {
  const pageSize = url.searchParams.get('page_size');
  if (pageSize === null) return;
  // Exact path or a sub-path of it (e.g. /api/v1/issues/webhooks is a different
  // endpoint, so require an exact match or a trailing slash).
  const match = LIMIT_PAGINATED_ENDPOINTS.some(
    p => url.pathname === p || url.pathname === `${p}/`,
  );
  if (!match) return;
  if (!url.searchParams.has('limit')) url.searchParams.set('limit', pageSize);
  url.searchParams.delete('page_size');
}

class ApiClient {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  private buildUrl(endpoint: string, params?: Record<string, unknown>): string {
    // When baseUrl is empty (same-origin mode), resolve relative endpoints against the
    // page origin — new URL(endpoint, '') throws on an empty base string.
    const base = this.baseUrl || window.location.origin;
    const url = new URL(endpoint, base);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => url.searchParams.append(key, String(v)));
          } else {
            url.searchParams.append(key, String(value));
          }
        }
      });
    }

    normalizePageSizeParam(url);
    return url.toString();
  }

  private async withRetry<T>(
    fn: () => Promise<T>,
    retries: number = API_CONFIG.RETRY_COUNT ?? 1
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let i = 0; i < retries; i++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as Error;
        if (i < retries - 1) {
          await new Promise(resolve =>
            setTimeout(resolve, API_CONFIG.RETRY_DELAY * Math.pow(2, i))
          );
        }
      }
    }

    throw lastError;
  }

  private async requestOnce<T>(endpoint: string, options: RequestOptions): Promise<ApiResponse<T>> {
    const { method = 'GET', body, params, headers = {}, signal, timeoutMs, rawBody } = options;

    const url = this.buildUrl(endpoint, params);

    // Log the API request (dev only — never in the production bundle, where a
    // single console.log of a multi-MB response body freezes DevTools and
    // roughly doubles the payload's memory cost).
    if (DEBUG) {
      console.log(`%c[Cloud Security API] ${method} ${url}`, 'color: #3b82f6; font-weight: bold');
      if (body) {
        console.log('%c[Cloud Security API] Request body:', 'color: #8b5cf6', body);
      }
    }

    // Attach auth token if available
    const token = localStorage.getItem('access_token');
    const authHeaders: Record<string, string> = token
      ? { 'Authorization': `Bearer ${token}` }
      : {};

    // Attach tenant ID if available (TenantContext is the source of truth at runtime;
    // localStorage is the persistence layer).
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      authHeaders['X-Tenant-ID'] = tenantId;
    }

    // Always outbound a correlation-id so server logs can stitch the request.
    const correlationId = newCorrelationId();
    authHeaders['X-Correlation-ID'] = correlationId;

    const controller = new AbortController();
    const effectiveTimeout = typeof timeoutMs === 'number' && timeoutMs > 0 ? timeoutMs : this.timeout;
    const timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);

    // Decide the body + content-type. Default is JSON.stringify with the
    // 'application/json' header. rawBody lets callers send CSV / FormData /
    // Blob / string verbatim with a custom Content-Type.
    const isFormDataOrBlob =
      typeof FormData !== 'undefined' && body instanceof FormData
      || typeof Blob !== 'undefined' && body instanceof Blob;
    const sendRaw = rawBody === true || isFormDataOrBlob;
    const baseHeaders: Record<string, string> = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
    };
    if (!sendRaw) {
      baseHeaders['Content-Type'] = 'application/json';
    }
    // For raw FormData uploads, let the browser set the multipart boundary —
    // do NOT override Content-Type. For raw text/blob with explicit header,
    // the caller's `headers` value below will win.

    try {
      const response = await fetch(url, {
        method,
        headers: {
          ...baseHeaders,
          ...authHeaders,
          ...headers,
        },
        body: body == null ? undefined : (sendRaw ? (body as BodyInit) : JSON.stringify(body)),
        signal: signal || controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutId);

      // Server may echo correlation back; prefer server value when present.
      const serverCid = response.headers?.get('X-Correlation-ID') || correlationId;

      const responseOk =
        response.ok ??
        (typeof response.status === 'number' && response.status >= 200 && response.status < 300);
      if (!responseOk) {
        const errorData = await response.json().catch(() => ({} as Record<string, unknown>));
        // Wiz envelope: { error: { code, message, details } }
        const env = (errorData as { error?: WizErrorEnvelope }).error;
        const flat = errorData as Record<string, unknown>;
        const message =
          env?.message ||
          (flat.detail as string | undefined) ||
          (flat.message as string | undefined) ||
          `HTTP ${response.status}: ${response.statusText}`;
        const code =
          env?.code ||
          (flat.code as string | undefined) ||
          `HTTP_${response.status}`;
        // 503 = service down / circuit breaker open. This is an expected,
        // recoverable condition (e.g. an optional microservice isn't running),
        // so log it quietly — callers degrade gracefully. Real errors (4xx,
        // 500/502/504) stay loud.
        // 401 = token missing/expired/invalid. Trigger the global session-expiry
        // flow so the whole app re-auths once instead of every panel rendering
        // its own auth error. (Only meaningful when a token was actually sent;
        // pre-login probes have none and are harmless no-ops.)
        if (response.status === 401 && token) {
          handleUnauthorized();
        }
        if (response.status === 503) {
          console.debug(
            `%c[Cloud Security API] ${code}: ${message} — service unavailable, skipping (cid=${serverCid})`,
            'color: #9ca3af',
          );
        } else {
          console.log(
            `%c[Cloud Security API] Error ${response.status} ${code}: ${message} (cid=${serverCid})`,
            'color: #ef4444; font-weight: bold',
          );
        }
        return {
          data: null,
          error: message,
          errorCode: code,
          errorDetails: env?.details ?? null,
          status: response.status,
          correlationId: serverCid,
        };
      }

      // Tolerate empty bodies (HTTP 204 / 205 / explicit Content-Length: 0)
      // and bodies the server forgot to JSON-encode. Calling response.json()
      // on these throws "Unexpected end of JSON input" — bug fix for the
      // DELETE /api/v1/cloud-accounts/{id} flow which returns 204 No Content.
      const contentLength = response.headers?.get('content-length');
      const contentType = (response.headers?.get('content-type') || '').toLowerCase();
      let data: unknown = null;
      if (
        response.status !== 204 &&
        response.status !== 205 &&
        contentLength !== '0'
      ) {
        try {
          if (contentType.includes('application/json')) {
            data = await response.json();
          } else {
            // Server returned a non-JSON body (text/html, plain text, etc.).
            // Try JSON anyway in case Content-Type was missing; fall back to text.
            const text = await response.text();
            if (text) {
              try {
                data = JSON.parse(text);
              } catch {
                data = { _raw: text };
              }
            }
          }
        } catch {
          // Empty body or malformed JSON — treat as no content.
          data = null;
        }
      }
      // R196 (2026-06-04): gateway tags fallback responses with X-Fallback
      // when the upstream service is unreachable / not registered. Surface
      // this to callers so e.g. the Deployments page can show "Connector
      // service offline" instead of "No deployments yet" on the empty stub.
      const isFallback = response.headers?.get('X-Fallback') === 'true';
      const fallbackReason = response.headers?.get('X-Fallback-Reason') || undefined;
      const fallbackUpstream = response.headers?.get('X-Fallback-Upstream') || undefined;
      if (isFallback) {
        console.warn(
          `%c[Cloud Security API] Fallback response — upstream unreachable (reason=${fallbackReason ?? 'unknown'}, upstream=${fallbackUpstream ?? 'n/a'})`,
          'color: #f59e0b; font-weight: bold',
        );
      } else if (DEBUG) {
        // Log status only, never the body — `/api/v1/issues` alone is ~10MB and
        // serializing it into the console froze the busiest page.
        console.log(`%c[Cloud Security API] Response ${response.status} ${method} ${url}`, 'color: #22c55e; font-weight: bold');
      }
      return {
        data: data as T | null,
        error: null,
        status: response.status,
        correlationId: serverCid,
        isFallback,
        fallbackReason,
        fallbackUpstream,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if ((error as Error).name === 'AbortError') {
        console.log('%c[Cloud Security API] Request timeout', 'color: #f59e0b; font-weight: bold');
        return {
          data: null,
          error: 'Request timeout',
          status: 408,
          correlationId,
        };
      }

      console.log('%c[Cloud Security API] Network error:', 'color: #ef4444; font-weight: bold', (error as Error).message);
      return {
        data: null,
        error: (error as Error).message || 'Network error',
        status: 0,
        correlationId,
      };
    }
  }

  /**
   * In-flight GETs, keyed by endpoint+params. Two panels asking for the same
   * thing at the same moment share one request.
   *
   * Measured 2026-07-27 across a 70-route sweep: 28 redundant calls, worst
   * being /cspm firing GET /api/v1/cspm/overview SIX times in one page load
   * (several components each own their fetch). That is not just waste — during
   * a live AWS scan the box ran at load-average 17.7 on 8 cores and those
   * bursts were exactly when endpoints started returning 500/503. Every one of
   * them answered 200 in under 0.2s when asked once.
   *
   * Abort is reference-counted: the shared request runs on its own controller
   * and is only aborted once EVERY subscriber has aborted, so one component
   * unmounting can no longer cancel a request another component is still
   * waiting on — which is the bug a naive promise-cache would introduce.
   */
  private inflightGets = new Map<string, {
    promise: Promise<ApiResponse<unknown>>;
    controller: AbortController;
    subscribers: number;
  }>();

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    const { method = 'GET', retry } = options;
    if (method === 'GET') {
      const coalesced = this.coalesceGet<T>(endpoint, options);
      if (coalesced) return coalesced;
    }
    return this.requestUncoalesced<T>(endpoint, options);
  }

  private coalesceGet<T>(endpoint: string, options: RequestOptions): Promise<ApiResponse<T>> | null {
    const key = `${endpoint}?${JSON.stringify(options.params ?? null)}`;
    const callerSignal = options.signal;
    if (callerSignal?.aborted) return null;   // nothing to share; let it fail normally

    let entry = this.inflightGets.get(key);
    if (!entry) {
      const controller = new AbortController();
      const promise = this.requestUncoalesced<T>(endpoint, { ...options, signal: controller.signal })
        .finally(() => { this.inflightGets.delete(key); }) as Promise<ApiResponse<unknown>>;
      entry = { promise, controller, subscribers: 0 };
      this.inflightGets.set(key, entry);
    }
    entry.subscribers += 1;
    const shared = entry;
    if (callerSignal) {
      callerSignal.addEventListener('abort', () => {
        shared.subscribers -= 1;
        if (shared.subscribers <= 0) shared.controller.abort();
      }, { once: true });
    }
    return shared.promise as Promise<ApiResponse<T>>;
  }

  private async requestUncoalesced<T>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    const { method = 'GET', retry } = options;
    // Default: retry idempotent GETs only. Opt-in via options.retry for others.
    const shouldRetry = retry === undefined ? method === 'GET' : retry;
    // Single funnel: get/post/put/patch/delete all land here, so this is the one
    // place that can guarantee a failure is never silent (see
    // signalRequestFailure). Signalled AFTER retries are exhausted, so a request
    // that recovers on attempt 2 never raises a banner.
    const announce = (r: ApiResponse<T>): ApiResponse<T> => {
      if (r.error) {
        signalRequestFailure({
          endpoint, status: r.status, message: r.error, correlationId: r.correlationId,
          at: Date.now(),
        });
      }
      return r;
    };
    if (!shouldRetry) {
      return announce(await this.requestOnce<T>(endpoint, options));
    }
    let attempt: ApiResponse<T> | null = null;
    await this.withRetry(async () => {
      attempt = await this.requestOnce<T>(endpoint, options);
      // Treat transient 5xx + 0 (network) + 408 as retryable. 4xx are caller
      // errors → fail fast. 503 means the service is down / its circuit breaker
      // is open: retrying within the same render just re-hits the open breaker
      // and spams the console, so fail fast and let the caller degrade.
      const retryable =
        (attempt.status >= 500 && attempt.status !== 503) ||
        attempt.status === 0 ||
        attempt.status === 408;
      if (retryable) {
        throw new Error(attempt.error || `retryable ${attempt.status}`);
      }
      return attempt;
    }).catch(() => undefined);
    if (attempt === null) {
      return announce({
        data: null,
        error: 'Request failed',
        status: 0,
        correlationId: '',
      });
    }
    return announce(attempt as unknown as ApiResponse<T>);
  }

  async get<T>(endpoint: string, params?: Record<string, unknown>, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', params, signal });
  }

  async post<T>(endpoint: string, body?: unknown, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body, params });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Download file as blob (for CSV, PDF, etc.)
  async download(endpoint: string, params?: Record<string, unknown>): Promise<ApiResponse<Blob>> {
    const url = this.buildUrl(endpoint, params);

    console.log(`%c[Cloud Security API] DOWNLOAD ${url}`, 'color: #3b82f6; font-weight: bold');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Attach auth token if available
    const dlToken = localStorage.getItem('access_token');
    const dlAuthHeaders: Record<string, string> = dlToken
      ? { 'Authorization': `Bearer ${dlToken}` }
      : {};
    // Scope the download to the active tenant (parity with request()); without
    // this, server-side exports fall back to the "default" tenant.
    const dlTenantId = localStorage.getItem('tenant_id');
    if (dlTenantId) {
      dlAuthHeaders['X-Tenant-ID'] = dlTenantId;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: dlAuthHeaders,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log(`%c[Cloud Security API] Download Error ${response.status}: ${errorData.detail || response.statusText}`, 'color: #ef4444; font-weight: bold');
        return {
          data: null,
          error: errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
        };
      }

      const blob = await response.blob();
      console.log(`%c[Cloud Security API] Download complete - ${blob.size} bytes`, 'color: #22c55e; font-weight: bold');
      return {
        data: blob,
        error: null,
        status: response.status,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if ((error as Error).name === 'AbortError') {
        console.log('%c[Cloud Security API] Download timeout', 'color: #f59e0b; font-weight: bold');
        return {
          data: null,
          error: 'Download timeout',
          status: 408,
        };
      }

      console.log('%c[Cloud Security API] Download error:', 'color: #ef4444; font-weight: bold', (error as Error).message);
      return {
        data: null,
        error: (error as Error).message || 'Network error',
        status: 0,
      };
    }
  }
}

export const apiClient = new ApiClient();
export default apiClient;
